import React from 'react';
import PropTypes from 'prop-types';
import { minBy } from 'lodash';
import './css/HotelsMap.css';
import { loadGoogleMaps, getGoogleMapId } from '../../../Utils/GoogleMapsUtils';
import { formatPrice } from '../../../Utils/CommonUtils';
import { SYSTEM_FAULT } from '../../../Utils/Constants/GlobalConstants';

const SINGLE_HOTEL_ZOOM = 14;
// zoom for a hotel selected in the list
const FOCUS_HOTEL_ZOOM = 15;
// starting from this zoom every hotel is shown with its price
const MAX_CLUSTER_ZOOM = 17;
// distance in screen pixels at which price markers overlap
const CLUSTER_DISTANCE_X = 120;
const CLUSTER_DISTANCE_Y = 44;
const FIT_BOUNDS_PADDING = 40;
// flight animation between hotels
const FLY_DURATION = 1200;
const MAX_FLY_ZOOM_OUT = 6;
const CLUSTER_FIT_BOUNDS_PADDING = 80;
// small countries (Qatar, Maldives) shouldn't be shown too close
const MAX_COUNTRY_ZOOM = 9;

function lerp(from, to, k) {
  return from + (to - from) * k;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function getHotelPosition(hotel) {
  return {
    lat: Number(hotel.latitude),
    lng: Number(hotel.longitude),
  };
}

function getMarkerClass(isActive, isCheapest) {
  if (isActive) {
    return 'hm_marker hm_marker_active';
  }

  return isCheapest ? 'hm_marker hm_marker_cheapest' : 'hm_marker';
}

function createMarkerContent(hotel, isActive, isCheapest) {
  const content = document.createElement('div');
  content.className = getMarkerClass(isActive, isCheapest);
  content.textContent = `от ${formatPrice(hotel.price)} ${hotel.currency}`;

  return content;
}

function createClusterContent(count) {
  const content = document.createElement('div');
  content.className = 'hm_cluster';
  content.textContent = count;

  return content;
}

class HotelsMap extends React.Component {
  constructor(props) {
    super(props);
    this.mapNode = React.createRef();
    this.map = null;
    this.mapsLib = null;
    this.markers = [];
    this.renderedZoom = null;
    this.flyAnimation = null;
    this.mapListeners = [];
    this.cheapestHotelId = null;
    this.isCountryShown = false;

    this.state = {
      isError: false,
    };
  }

  componentDidMount() {
    loadGoogleMaps()
      .then((mapsLib) => {
        if (!this.mapNode.current) {
          return;
        }

        this.mapsLib = mapsLib;
        this.map = new mapsLib.Map(this.mapNode.current, {
          mapId: getGoogleMapId(),
          center: { lat: 0, lng: 0 },
          zoom: 2,
          disableDefaultUI: true,
          zoomControl: true,
          // bottom right corner is taken by the hotel card
          zoomControlOptions: {
            position: mapsLib.ControlPosition.RIGHT_CENTER,
          },
          clickableIcons: false,
          gestureHandling: 'greedy',
        });

        // clusters depend only on zoom, so panning doesn't redraw markers
        this.mapListeners = [
          this.map.addListener('idle', () => {
            if (!this.map) {
              return;
            }

            // projection is ready only after the first render, so the first
            // flight to the country starts here
            if (!this.isCountryShown) {
              this.isCountryShown = true;
              this.showCountry();
            }

            if (this.map.getZoom() !== this.renderedZoom) {
              this.renderMarkers();
            }
          }),
          this.map.addListener('click', () => {
            const { onHotelSelect } = this.props;
            onHotelSelect(null);
          }),
        ];

        this.updateHotels(true);
      })
      .catch(() => {
        this.setState({ isError: true });
      });
  }

  componentDidUpdate(prevProps) {
    const { hotels, selectedHotelId, focus, countryBounds } = this.props;
    const isHotelsCleared = prevProps.hotels.length > 0 && hotels.length === 0;

    // new search: the map waits for hotels over the searched country
    if (
      this.isCountryShown &&
      (prevProps.countryBounds !== countryBounds || isHotelsCleared)
    ) {
      this.showCountry();
    }

    if (this.map && prevProps.hotels !== hotels) {
      // fit bounds only for a new search, not for "search more"
      this.updateHotels(prevProps.hotels.length === 0);
    }

    // new focus object is passed on every click on a hotel in the list
    if (focus && prevProps.focus !== focus) {
      this.focusHotel(focus.hotel);
    }

    // selected hotel is never clustered, so markers are rebuilt
    if (this.map && prevProps.selectedHotelId !== selectedHotelId) {
      this.renderMarkers();
    }
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.flyAnimation);
    this.mapListeners.forEach((listener) => listener.remove());
    this.clearMarkers();
    this.map = null;
  }

  getHotelsWithCoords = () => {
    const { hotels } = this.props;

    return hotels.filter((item) => item.latitude && item.longitude);
  };

  getBounds = (hotels) => {
    const { LatLngBounds } = this.mapsLib;
    const bounds = new LatLngBounds();

    hotels.forEach((hotel) => bounds.extend(getHotelPosition(hotel)));

    return bounds;
  };

  // greedy grid clustering in world pixel coordinates of the current zoom
  getClusters = () => {
    const { selectedHotelId } = this.props;
    const projection = this.map.getProjection();
    const zoom = this.map.getZoom();
    const scale = 2 ** zoom;
    const isClustering = zoom < MAX_CLUSTER_ZOOM;
    const clusters = [];

    this.getHotelsWithCoords().forEach((hotel) => {
      const point = projection.fromLatLngToPoint(getHotelPosition(hotel));
      const x = point.x * scale;
      const y = point.y * scale;
      // selected and cheapest hotels are always shown with their price
      const isSeparate =
        hotel.id === selectedHotelId || hotel.id === this.cheapestHotelId;
      const cluster =
        isClustering &&
        !isSeparate &&
        clusters.find(
          (item) =>
            !item.isSeparate &&
            Math.abs(item.x - x) < CLUSTER_DISTANCE_X &&
            Math.abs(item.y - y) < CLUSTER_DISTANCE_Y,
        );

      if (cluster) {
        cluster.hotels.push(hotel);
      } else {
        clusters.push({ x, y, isSeparate, hotels: [hotel] });
      }
    });

    return clusters;
  };

  clearMarkers = () => {
    this.markers.forEach((marker) => {
      // eslint-disable-next-line no-param-reassign
      marker.map = null;
    });
    this.markers = [];
  };

  focusHotel = (hotel) => {
    if (!this.map || !hotel.latitude || !hotel.longitude) {
      return;
    }

    this.flyTo(
      getHotelPosition(hotel),
      Math.max(this.map.getZoom(), FOCUS_HOTEL_ZOOM),
    );
  };

  // the farther the target, the more the map zooms out in the middle of flight
  getFlyZoomOut = (from, to) => {
    const projection = this.map.getProjection();
    const fromPoint = projection.fromLatLngToPoint(from);
    const toPoint = projection.fromLatLngToPoint(to);
    const distance =
      Math.hypot(toPoint.x - fromPoint.x, toPoint.y - fromPoint.y) *
      2 ** this.map.getZoom();
    const screens = distance / this.mapNode.current.offsetWidth;

    return Math.min(Math.log2(screens + 1), MAX_FLY_ZOOM_OUT);
  };

  flyTo = (position, zoom, isZoomOut = true) => {
    cancelAnimationFrame(this.flyAnimation);

    if (!this.map.getProjection()) {
      this.map.setCenter(position);
      this.map.setZoom(zoom);
      return;
    }

    const startCenter = this.map.getCenter().toJSON();
    const startZoom = this.map.getZoom();
    const zoomOut = isZoomOut
      ? this.getFlyZoomOut(startCenter, position)
      : 0;
    const startTime = performance.now();

    const step = (now) => {
      const t = Math.min((now - startTime) / FLY_DURATION, 1);
      const k = easeInOut(t);

      this.map.moveCamera({
        center: {
          lat: lerp(startCenter.lat, position.lat, k),
          lng: lerp(startCenter.lng, position.lng, k),
        },
        zoom: lerp(startZoom, zoom, k) - zoomOut * Math.sin(Math.PI * k),
      });

      if (t < 1 && this.map) {
        this.flyAnimation = requestAnimationFrame(step);
      }
    };

    this.flyAnimation = requestAnimationFrame(step);
  };

  // same as fitBounds, but returns camera to animate to
  getBoundsCamera = (bounds, padding, maxZoom) => {
    const projection = this.map.getProjection();
    const northEast = projection.fromLatLngToPoint(bounds.getNorthEast());
    const southWest = projection.fromLatLngToPoint(bounds.getSouthWest());
    const { offsetWidth, offsetHeight } = this.mapNode.current;
    const width = offsetWidth - padding * 2;
    const height = offsetHeight - padding * 2;
    const dx = Math.abs(northEast.x - southWest.x);
    const dy = Math.abs(northEast.y - southWest.y);
    let zoom = maxZoom;

    // hotels with the same coordinates can't be split, show them with prices
    if (dx > 0 || dy > 0) {
      const scale = Math.min(width / dx, height / dy);
      zoom = Math.min(Math.log2(scale), maxZoom);
    }

    return { center: bounds.getCenter().toJSON(), zoom };
  };

  // until hotels are found the map shows the searched country
  showCountry = () => {
    const { countryBounds } = this.props;

    if (
      !this.map ||
      !this.map.getProjection() ||
      !countryBounds ||
      this.getHotelsWithCoords().length > 0
    ) {
      return;
    }

    const { LatLngBounds } = this.mapsLib;
    const { center, zoom } = this.getBoundsCamera(
      new LatLngBounds(countryBounds),
      FIT_BOUNDS_PADDING,
      MAX_COUNTRY_ZOOM,
    );

    this.flyTo(center, zoom, false);
  };

  handleClusterClick = (hotels) => {
    const { center, zoom } = this.getBoundsCamera(
      this.getBounds(hotels),
      CLUSTER_FIT_BOUNDS_PADDING,
      MAX_CLUSTER_ZOOM,
    );

    this.flyTo(center, Math.max(zoom, this.map.getZoom() + 1), false);
  };

  renderHotelMarker = (hotel) => {
    const { selectedHotelId, onHotelSelect } = this.props;
    const { AdvancedMarkerElement } = this.mapsLib;
    const isActive = hotel.id === selectedHotelId;
    const isCheapest = hotel.id === this.cheapestHotelId;
    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: getHotelPosition(hotel),
      title: hotel.name,
      content: createMarkerContent(hotel, isActive, isCheapest),
      zIndex: isActive || isCheapest ? 1 : null,
    });

    marker.addListener('click', () => onHotelSelect(hotel));

    return marker;
  };

  renderClusterMarker = (hotels) => {
    const { AdvancedMarkerElement } = this.mapsLib;
    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: this.getBounds(hotels).getCenter(),
      content: createClusterContent(hotels.length),
    });

    marker.addListener('click', () => this.handleClusterClick(hotels));

    return marker;
  };

  renderMarkers = () => {
    // projection is available only after the first map render
    if (!this.map.getProjection()) {
      return;
    }

    const cheapestHotel = minBy(this.getHotelsWithCoords(), 'price');

    this.clearMarkers();
    this.renderedZoom = this.map.getZoom();
    this.cheapestHotelId = cheapestHotel ? cheapestHotel.id : null;

    this.markers = this.getClusters().map((cluster) => {
      if (cluster.hotels.length === 1) {
        return this.renderHotelMarker(cluster.hotels[0]);
      }

      return this.renderClusterMarker(cluster.hotels);
    });
  };

  updateHotels = (isFitBounds) => {
    const hotels = this.getHotelsWithCoords();

    this.renderMarkers();

    if (!isFitBounds || hotels.length === 0) {
      return;
    }

    const bounds = this.getBounds(hotels);

    // map isn't rendered yet, nothing to animate from
    if (!this.map.getProjection()) {
      if (hotels.length === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(SINGLE_HOTEL_ZOOM);
      } else {
        this.map.fitBounds(bounds, FIT_BOUNDS_PADDING);
      }

      return;
    }

    // smooth flight from the country to the found hotels
    if (hotels.length === 1) {
      this.flyTo(getHotelPosition(hotels[0]), SINGLE_HOTEL_ZOOM, false);
    } else {
      const { center, zoom } = this.getBoundsCamera(
        bounds,
        FIT_BOUNDS_PADDING,
        MAX_CLUSTER_ZOOM,
      );

      this.flyTo(center, zoom, false);
    }
  };

  render() {
    const { isError } = this.state;

    return (
      <div className="hm_wrapper">
        {isError && <div className="hm_error gr_text_14">{SYSTEM_FAULT}</div>}
        <div className="hm_map" ref={this.mapNode} />
      </div>
    );
  }
}

HotelsMap.propTypes = {
  hotels: PropTypes.instanceOf(Array).isRequired,
  selectedHotelId: PropTypes.number,
  focus: PropTypes.instanceOf(Object),
  countryBounds: PropTypes.instanceOf(Object),
  onHotelSelect: PropTypes.func.isRequired,
};

HotelsMap.defaultProps = {
  selectedHotelId: null,
  focus: null,
  countryBounds: null,
};

export default HotelsMap;
